/**
 * <exclusive-drop> — customer-tag-gated "exclusive drop" buy / register modal.
 *
 * Rendered by blocks/buy-buttons-custom.liquid when a product sets custom.access_segment.
 * One PDP button opens a dialog whose contents are decided SERVER-SIDE by eligibility:
 *   - Eligible (logged in + customer.tags ∩ access_segment): a "Secure yours" button →
 *     enforce the per-customer limit against the LIVE cart, then POST /cart/add.js (qty 1)
 *     → /checkout.
 *   - Not eligible: "Register for a chance to purchase" → Klaviyo client subscription tagged
 *     with a per-drop custom_source so the merchant can segment entrants and draw winners.
 *
 * Registration is TWO-PATH. a.klaviyo.com is on the major ad/privacy blocklists, so for shoppers
 * running a blocker the client subscription fetch throws (the old dead-end "Network error").
 * On network-level failure (or Klaviyo 5xx) the component reveals the server-rendered first-party
 * rescue form in the snippet ([data-ed-fallback]: native /contact customer form, per-drop tags),
 * prefilled with the entered email. Its real user-clicked submit rides Shopify's spam-protection
 * pipeline and navigates; a sessionStorage marker ("ed-reg-pending") lets connectedCallback reopen
 * the dialog on return and show the registered state when ?customer_posted=true comes back.
 *
 * Per-customer limit (data-limit, default 1) is enforced cart-aware: /cart.js is the source of
 * truth, so going back / refreshing / clicking again can't stack more than the limit (mirrors
 * the pre-order guard). Still client-trust (Tier 1): a crafted /cart/add.js can bypass it —
 * pair with the order-backstop Flow / a Tier-2 Validation Function. The cart quantity selector
 * is also capped (snippets/quantity-selector.liquid). See features/exclusive-limited-edition.md.
 */
if (!customElements.get('exclusive-drop')) {
  customElements.define(
    'exclusive-drop',
    class ExclusiveDrop extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('.exclusive-drop__dialog');
        this.variantId = parseInt(this.getAttribute('data-variant-id'), 10) || 0;
        this.limit = parseInt(this.getAttribute('data-limit'), 10) || 1;
        this.handle = this.getAttribute('data-handle') || '';
        this.controller = new AbortController();
        const { signal } = this.controller;
        this.addEventListener(
          'click',
          (event) => {
            if (event.target.closest('[data-ed-open]')) {
              event.preventDefault();
              this.openDialog();
            } else if (event.target.closest('[data-ed-close]')) {
              event.preventDefault();
              this.closeDialog();
            } else if (event.target.closest('[data-ed-buy]')) {
              event.preventDefault();
              this.buy(event.target.closest('[data-ed-buy]'));
            } else if (event.target.closest('[data-ed-register]')) {
              event.preventDefault();
              this.register(event.target.closest('[data-ed-register]'));
            } else if (event.target.closest('[data-ed-checkout]')) {
              event.preventDefault();
              this.goToCheckout(event.target.closest('[data-ed-checkout]'));
            }
          },
          { signal }
        );
        // Back from checkout via the bfcache: allow "Go to checkout" again.
        window.addEventListener('pageshow', () => (this.leaving = false), { signal });
        // Re-check on cart changes and on connect so back / refresh / re-open reflects the cart.
        document.addEventListener('cart:update', () => this.refreshLimit(), { signal });
        this.refreshLimit();
        // Rescue-path bookkeeping: mark the navigation so we can restore the dialog on return.
        const fallbackForm = this.querySelector('[data-ed-fallback] form');
        if (fallbackForm) {
          fallbackForm.addEventListener(
            'submit',
            () => {
              try {
                sessionStorage.setItem('ed-reg-pending', this.handle);
              } catch (e) {
                /* storage blocked — return trip just won't auto-reopen */
              }
            },
            { signal }
          );
        }
        this.resumeAfterFallback();
      }

      // After the first-party rescue form navigates (POST /contact → redirect back, possibly via
      // Shopify's challenge page), reopen the dialog: registered state if ?customer_posted=true,
      // otherwise re-show the rescue form (server-rendered form.errors included).
      resumeAfterFallback() {
        let pending = null;
        try {
          pending = sessionStorage.getItem('ed-reg-pending');
        } catch (e) {
          return;
        }
        if (!pending || pending !== this.handle) return;
        try {
          sessionStorage.removeItem('ed-reg-pending');
        } catch (e) {
          /* ignore */
        }
        if (/[?&]customer_posted=true/.test(window.location.search)) {
          this.markRegistered();
        } else {
          this.showFallback('');
          this.showMessage('Your registration didn’t complete — please try again below.');
        }
        this.openDialog();
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
      }

      openDialog() {
        if (!this.dialog) return;
        if (this.dialog.showModal) this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
      }

      closeDialog() {
        if (!this.dialog) return;
        if (this.dialog.close) this.dialog.close();
        else this.dialog.removeAttribute('open');
      }

      cartQtyForVariant() {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => {
            let qty = 0;
            (cart.items || []).forEach((item) => {
              if (String(item.variant_id) === String(this.variantId)) qty += item.quantity;
            });
            return qty;
          })
          .catch(() => 0);
      }

      // Lock the buy button once the per-customer limit is already in the cart.
      refreshLimit() {
        const button = this.querySelector('[data-ed-buy]');
        if (!button || !this.variantId) return;
        this.cartQtyForVariant().then((qty) => {
          if (qty >= this.limit) this.lockBuy(button);
        });
      }

      lockBuy(button) {
        button.disabled = true;
        this.revealCheckout();
        this.showMessage('You’ve already secured yours — limit ' + this.limit + ' per customer on this drop.');
      }

      // The drop skips /cart, where Shipping Protection re-tiers its line. Correct the tier first
      // (shared helper, assets/shipping-protection-tier.js); any failure still goes to checkout.
      goToCheckout(link) {
        if (this.leaving) return;
        this.leaving = true;
        const href = link.getAttribute('href') || '/checkout';
        import('@theme/shipping-protection-tier')
          .then((module) => module.ensureShippingProtectionTier({ path: 'drop' }))
          .catch(() => null)
          .then(() => {
            window.location.href = href;
          });
      }

      revealCheckout() {
        const checkout = this.querySelector('[data-ed-checkout]');
        if (checkout) checkout.hidden = false;
      }

      buy(button) {
        if (!this.variantId) return;
        button.disabled = true;
        this.cartQtyForVariant()
          .then((qty) => {
            if (qty + 1 > this.limit) {
              this.lockBuy(button);
              return null;
            }
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: this.variantId, quantity: 1 }] }),
            });
          })
          .then((response) => {
            if (!response) return; // blocked by limit
            if (!response.ok) throw new Error('add failed');
            this.revealCheckout();
            this.showMessage('Secured — it’s in your cart.');
          })
          .catch(() => {
            button.disabled = false;
            this.showMessage('Could not add to cart — please try again.');
          });
      }

      register(button) {
        const form = button.closest('.exclusive-drop__register');
        const emailEl = form ? form.querySelector('input[type="email"]') : null;
        const email = emailEl && emailEl.value ? emailEl.value.trim() : '';
        if (!email || email.indexOf('@') === -1) {
          this.showMessage('Please enter a valid email.');
          return;
        }
        const listId = this.getAttribute('data-klaviyo-list-id');
        const pubKey = this.getAttribute('data-klaviyo-public-key');
        const handle = this.handle;
        if (!listId || !pubKey) {
          // Misconfigured: no list to subscribe to. Don't fake success.
          console.error('[exclusive-drop] Klaviyo not wired — list id="' + (listId || '') + '" public key="' + (pubKey || '') + '". Registration NOT captured. Set exclusive_drop_list_id on the Buy Buttons block.');
          this.showMessage('Registration is temporarily unavailable — please try again shortly.');
          return;
        }
        const url = 'https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(pubKey);
        const payload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Exclusive drop – ' + handle,
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        };
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            Accept: 'application/vnd.api+json',
            revision: '2025-07-15',
          },
          body: JSON.stringify(payload),
        })
          .then((res) => {
            if (res.ok) {
              // 202 Accepted. NOTE: if the list uses DOUBLE opt-in, the profile is held
              // pending email confirmation and won't appear on the list until confirmed.
              this.markRegistered();
              return;
            }
            if (res.status >= 500) {
              console.error('[exclusive-drop] Klaviyo 5xx — routing to the first-party rescue form.', res.status);
              this.showFallback(email);
              return;
            }
            return res.text().then((body) => {
              console.error('[exclusive-drop] Klaviyo subscription rejected —', res.status, body);
              this.showMessage('Something went wrong — please try again.');
            });
          })
          .catch((err) => {
            // Network-level failure: for shoppers running ad/privacy blockers this ALWAYS happens
            // (a.klaviyo.com is blocklisted), so retrying is futile — reveal the first-party form.
            console.warn('[exclusive-drop] Klaviyo unreachable (blocker / offline) — routing to the first-party rescue form.', err);
            this.showFallback(email);
          });
      }

      // Success state shared by both registration paths.
      markRegistered() {
        const form = this.querySelector('.exclusive-drop__register');
        if (!form) return;
        const p = document.createElement('p');
        p.className = 'exclusive-drop__done';
        p.textContent = 'You’re registered — we’ll be in touch if you’re selected to purchase.';
        form.replaceChildren(p);
      }

      // Reveal the server-rendered first-party rescue form ([data-ed-fallback], native /contact
      // customer form) in place of the Klaviyo row. Must stay a real user-clicked submit — this
      // store has Shopify spam protection ON and AJAX posts to /contact 400 ("Missing CAPTCHA token").
      showFallback(email) {
        const wrap = this.querySelector('[data-ed-fallback]');
        if (!wrap) {
          this.showMessage('Network error — please try again.');
          return;
        }
        const label = this.querySelector('.exclusive-drop__reg-label');
        const row = this.querySelector('.exclusive-drop__register > .exclusive-drop__reg-row');
        // Inline display, not the hidden attribute — the theme's .exclusive-drop__reg-row
        // { display: flex } (and __reg-label { display: block }) would override [hidden].
        if (label) label.style.display = 'none';
        if (row) row.style.display = 'none';
        wrap.hidden = false;
        const input = wrap.querySelector('input[type="email"]');
        if (input) {
          if (email) input.value = email;
          input.focus();
        }
        this.showMessage('Your browser blocked our registration service (usually an ad or privacy blocker). No problem — hit “Finish registration” to register directly with us.');
      }

      showMessage(text) {
        let el = this.querySelector('.exclusive-drop__msg');
        if (!el) {
          el = document.createElement('p');
          el.className = 'exclusive-drop__msg';
          (this.dialog || this).appendChild(el);
        }
        el.textContent = text;
      }
    }
  );
}
